import React, {useEffect, useMemo, useState} from 'react'
import styled from 'styled-components'
import {ButtonHeader, Paragraph} from 'common/components'
import {GIFT_SHOP_HEADER_HEIGHT} from 'consts'
import {useTranslation} from 'i18n'
import convertUnit from 'lib/unit'
import {getBorder, getCollaboratorMember} from 'utils'
import {ForkygramUploadTagErrorType, UserInfoBase} from 'types'
import {ForkygramTemplateUploadForm} from '../../../../forkygram'
import {FototreeMemoryEditContentProps} from './FototreeContentProps'

const StyledSectionHeader = styled.div`
  display: flex;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  height: ${convertUnit(GIFT_SHOP_HEADER_HEIGHT)};
  border: ${({theme}) => getBorder(1, 'solid', theme.gray_1)};
`

const StyledButtonHeader = styled(ButtonHeader)`
  margin-left: auto;
`

export default function FototreeMemoryEditContent({
  stateEdit,
  dataTag,
  formRef,
  onPatchPost,
}: FototreeMemoryEditContentProps) {
  const {translate} = useTranslation()
  const stateFormError = useState<ForkygramUploadTagErrorType>('none')
  const [edit, setEdit] = stateEdit
  const formError = stateFormError[0]
  const stateCollaborator = useState<UserInfoBase | undefined>()
  const setCollaborator = stateCollaborator[1]
  const stateSearchCollaborator = useState(false)
  const [searchCollaborator, setSearchCollaborator] = stateSearchCollaborator

  const handleRenderHeader = useMemo(
    () => (
      <StyledSectionHeader>
        <ButtonHeader
          size={20}
          icon="back"
          color="primary_5"
          label={translate('global:back')}
          onClick={() =>
            searchCollaborator
              ? setSearchCollaborator(false)
              : setEdit(undefined)
          }
        />
        <Paragraph fontSize="l" fontWeight="medium">
          {translate('forkygram:uploadHeader')}
        </Paragraph>
        {searchCollaborator ? null : (
          <StyledButtonHeader
            size={20}
            icon="check"
            label={translate('global:save')}
            color={formError === 'none' ? 'primary_5' : 'gray_3'}
            onClick={formError === 'none' ? onPatchPost : undefined}
          />
        )}
      </StyledSectionHeader>
    ),
    [
      translate,
      searchCollaborator,
      formError,
      onPatchPost,
      setSearchCollaborator,
      setEdit,
    ],
  )

  const handleRenderContent = useMemo(
    () => (
      <ForkygramTemplateUploadForm
        stateFormError={stateFormError}
        editContent
        tag={dataTag}
        thumbnail={edit?.thumbnail}
        caption={edit?.caption}
        contentType={edit?.content_type}
        src={edit?.url}
        onFormUpdate={(item) => {
          formRef.current = item
        }}
        stateCollaborator={stateCollaborator}
        stateSearchCollaborator={stateSearchCollaborator}
        hasCustomHeader
      />
    ),
    [
      dataTag,
      edit?.caption,
      edit?.content_type,
      edit?.thumbnail,
      edit?.url,
      formRef,
      stateCollaborator,
      stateFormError,
      stateSearchCollaborator,
    ],
  )

  useEffect(() => {
    if (edit) {
      const {collaboratorMember} = getCollaboratorMember(
        edit.collaborator_members,
      )
      if (collaboratorMember !== null) {
        formRef.current?.setValue('collaborator_member_ids', [
          collaboratorMember.id,
        ])
        setCollaborator({
          id: collaboratorMember.id,
          photo: collaboratorMember.photo,
          username: collaboratorMember.username,
          creator_status: collaboratorMember.creator_status,
        })
      }
    }
  }, [edit, formRef, setCollaborator])

  return (
    <>
      {handleRenderHeader}
      <div>{handleRenderContent}</div>
    </>
  )
}
